/**
 * Runtime auth configuration.
 *
 * ONE Docker image promotes dev → qa → uat → prod; only per-env runtime config
 * differs. So the auth settings are NOT baked into the bundle at build time —
 * they are read from `window.__APP_CONFIG__`, which `public/config.js` defines
 * and `docker/40-runtime-config.sh` rewrites from pod env vars at container
 * start. Build-time `REACT_APP_*` vars are honoured as a fallback for local dev.
 *
 * Precedence: runtime config.js → build-time env → default below.
 *
 * Auth is OFF unless the environment turns it on (`AUTH_ENABLED=true`), so this
 * code ships everywhere and environments are enabled one at a time. With auth
 * off the UI is byte-identical to today's — <AuthGate> is a passthrough.
 *
 * NOTE: config.js is written once at container start, so a Portal env-var change
 * needs a pod recreate (new image SHA or `rollout restart`) — not a same-SHA
 * redeploy.
 */
import type { FeaturesConfig } from "./features";

/** EmailCreativeStudio_Dev app registration — SHARED by dev, qa and uat. */
const DEFAULT_TENANT_ID = "28e546c7-fd4b-4246-b0c1-c800d6381d7d";
const DEFAULT_CLIENT_ID = "041878e5-bd12-48ff-a8f5-496fdf2751b6";

export type AuthConfig = {
  enabled?: boolean;
  clientId?: string;
  tenantId?: string;
  /** API scope, e.g. `api://<client-id>/access_as_user`. Empty = no API token. */
  scope?: string;
  /** Override only if the app is not served from the origin root. */
  redirectUri?: string;
};

/**
 * Gateway (Email Creative Hub API) configuration.
 *
 * When `base` is set the app calls the CCOE API gateway per environment
 * (e.g. https://gateway-dev-aws-api.lennar.com) instead of the same-origin
 * `/api` proxy, attaching the Entra access token + `X-Client-Id` + `X-API-Key`
 * to every request (see services/api.ts). Empty = today's behaviour (/api).
 */
export type ApiConfig = {
  /** Gateway ORIGIN only — no path, no trailing slash. Per env. */
  base?: string;
  /** `X-Client-Id` header value. Defaults to the SPA clientId. */
  clientId?: string;
  /**
   * `X-API-Key` header value — the gateway usage-plan key for THIS
   * environment's consumer. Empty = the header is omitted entirely.
   *
   * NOT a secret: config.js is public, so whatever is set here ships to every
   * browser. It is a usage-plan/throttling handle; the Entra bearer is what
   * actually authenticates. Raw-URL GETs (`<img src>` /thumb) cannot carry it.
   */
  key?: string;
  /**
   * `"proxy"` = break-glass: call the same-origin `/api` proxy (nginx →
   * emailbuilder-be) instead of the gateway, even when `base` is set — honored
   * on NON-PROD hosts and localhost ONLY (services/api.ts refuses it on
   * production). Anything else (or unset) = normal gateway/`/api` resolution.
   */
  mode?: string;
};

/** `features` — UI switches, see services/features.ts (read at render time). */
export type AppConfig = { auth?: AuthConfig; api?: ApiConfig; features?: FeaturesConfig };

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

const runtime: AppConfig =
  (typeof window !== "undefined" && window.__APP_CONFIG__) || {};
/* CRA's DefinePlugin substitutes the whole `process.env` object into the
   bundle, so this works in the browser and under jest alike. */
const env: Record<string, string | undefined> = process.env;

const overrides: AuthConfig = runtime.auth ?? {};
const apiOverrides: ApiConfig = runtime.api ?? {};

export const AUTH_ENABLED: boolean =
  overrides.enabled ?? env.REACT_APP_AUTH_ENABLED === "true";

export const TENANT_ID: string =
  overrides.tenantId || env.REACT_APP_AUTH_TENANT_ID || DEFAULT_TENANT_ID;

export const CLIENT_ID: string =
  overrides.clientId || env.REACT_APP_AUTH_CLIENT_ID || DEFAULT_CLIENT_ID;

/**
 * The API scope requested for the ACCESS token.
 *
 * Deliberately empty by default: the app registration has no Application ID URI
 * yet (Entra → Expose an API), so `api://<client-id>/access_as_user` does not
 * exist and asking for it would fail the sign-in. Sign-in therefore requests
 * OIDC scopes only. Set AUTH_SCOPE once the scope is exposed and the gateway
 * authorizer is wired — no code change needed.
 */
export const API_SCOPE: string =
  overrides.scope ?? env.REACT_APP_AUTH_SCOPE ?? "";

/** Single-tenant authority — never `/common`. */
export const AUTHORITY = `https://login.microsoftonline.com/${TENANT_ID}`;

/**
 * Must be a registered SPA redirect URI, matched EXACTLY by Entra. `origin` has
 * no trailing slash, so `https://emailbuilder-dev.lennar.com` must be
 * registered as-is — a registered `.../` does not match (AADSTS50011).
 */
export const REDIRECT_URI: string =
  overrides.redirectUri ||
  env.REACT_APP_AUTH_REDIRECT_URI ||
  (typeof window !== "undefined" ? window.location.origin : "");

/**
 * Gateway ORIGIN for the Email Creative Hub API (no path, no trailing slash).
 * Empty by default → the app keeps calling the same-origin `/api` proxy. Set it
 * per env (dev/qa/uat gateway host) to route through the CCOE API gateway with
 * the Entra bearer + `X-Client-Id`. No code change to flip environments.
 */
export const API_BASE: string = (
  apiOverrides.base ||
  env.REACT_APP_API_BASE ||
  ""
).replace(/\/+$/, "");

/** `X-Client-Id` sent to the gateway — defaults to the SPA client id. */
export const API_CLIENT_ID: string =
  apiOverrides.clientId || env.REACT_APP_API_CLIENT_ID || CLIENT_ID;

/**
 * `X-API-Key` sent to the gateway (usage plan), per environment. Empty by
 * default so the header is omitted where the gateway does not enforce it.
 * Runtime key is `API_KEY_GATEWAY` (see docker/40-runtime-config.sh).
 */
export const API_KEY: string =
  apiOverrides.key || env.REACT_APP_API_KEY_GATEWAY || "";

/**
 * API routing mode — `"proxy"` forces the same-origin `/api` proxy (nginx →
 * emailbuilder-be) over the gateway on non-prod hosts; see ApiConfig.mode.
 * Empty/anything-else = normal resolution (gateway when API_BASE is set).
 */
export const API_MODE: string =
  (apiOverrides.mode || env.REACT_APP_API_MODE || "").toLowerCase();
